// User-facing settings for trigger notifications (email / SMS / sound / popup).

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  NotificationsActive as NotificationsActiveIcon,
  Email as EmailIcon,
  VolumeUp as VolumeUpIcon,
} from '@mui/icons-material';
import {
  getNotificationPreferences,
  sendTestNotification,
  updateNotificationPreferences,
} from '../../services/notificationService';
import {
  NotificationPreferences,
  defaultNotificationPreferences,
} from '../../types/notifications';
import {
  NOTIFICATION_PREFS_UPDATED_EVENT,
  showLocalNotification,
} from '../notifications/NotificationCenter';

const NotificationSettings: React.FC = () => {
  const [prefs, setPrefs] = useState<NotificationPreferences>(defaultNotificationPreferences());
  const [smtpConfigured, setSmtpConfigured] = useState(true);
  const [emailAddressesText, setEmailAddressesText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [feedback, setFeedback] = useState<{ severity: 'success' | 'error' | 'info'; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getNotificationPreferences();
        if (cancelled) return;
        setPrefs({
          email_notifications: data.email_notifications,
          sms_notifications: data.sms_notifications,
          dashboard_alerts: data.dashboard_alerts,
          trigger_events: data.trigger_events,
        });
        setEmailAddressesText((data.email_notifications.email_addresses || []).join(', '));
        setSmtpConfigured(data.smtp_configured);
      } catch {
        if (!cancelled) setFeedback({ severity: 'error', text: 'Could not load notification settings.' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const parseEmails = (text: string): string[] =>
    text
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const buildPayload = (): NotificationPreferences => ({
    ...prefs,
    email_notifications: {
      ...prefs.email_notifications,
      email_addresses: parseEmails(emailAddressesText),
    },
  });

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const saved = await updateNotificationPreferences(buildPayload());
      setPrefs({
        email_notifications: saved.email_notifications,
        sms_notifications: saved.sms_notifications,
        dashboard_alerts: saved.dashboard_alerts,
        trigger_events: saved.trigger_events,
      });
      setEmailAddressesText((saved.email_notifications.email_addresses || []).join(', '));
      setFeedback({ severity: 'success', text: 'Notification settings saved.' });
      window.dispatchEvent(new Event(NOTIFICATION_PREFS_UPDATED_EVENT));
    } catch {
      setFeedback({ severity: 'error', text: 'Failed to save notification settings.' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setFeedback(null);
    try {
      // Save first so the test uses the current form values.
      await updateNotificationPreferences(buildPayload());
      window.dispatchEvent(new Event(NOTIFICATION_PREFS_UPDATED_EVENT));
      // Show an immediate local popup + sound so the user can verify the in-app
      // channel works even if the realtime WebSocket isn't connected.
      showLocalNotification(
        'Test notification',
        'This is a local test popup. If you can see and hear this, in-app alerts work.',
        'success'
      );
      const result = await sendTestNotification();
      const channels = [
        result.email ? 'email' : null,
        result.sms ? 'SMS' : null,
        result.websocket ? 'in-app popup' : null,
      ].filter(Boolean);
      const base = channels.length
        ? `Test sent via: ${channels.join(', ')}.`
        : 'No channels are enabled yet.';

      // If email was attempted but the SMTP server rejected it, surface the exact reason.
      if (result.email_error) {
        const to = (result.email_recipients || []).join(', ');
        setFeedback({
          severity: 'error',
          text: `Email could NOT be sent${to ? ` to ${to}` : ''}: ${result.email_error}`,
        });
      } else {
        setFeedback({
          severity: result.message ? 'info' : 'success',
          text: result.message ? `${base} ${result.message}` : base,
        });
      }
    } catch {
      setFeedback({ severity: 'error', text: 'Failed to send test notification.' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <NotificationsActiveIcon sx={{ mr: 1 }} color="primary" />
          <Typography variant="h6">Trigger notifications</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Get notified when an indicator triggers or an execution webhook fires (or fails).
        </Typography>

        {!smtpConfigured && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Email/SMS delivery requires the server&apos;s outbound email (SMTP) to be configured. The
            in-app popup and sound work regardless.
          </Alert>
        )}

        {/* Events */}
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
          Notify me when…
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={prefs.trigger_events.execution_fired}
                onChange={(e) =>
                  setPrefs((p) => ({
                    ...p,
                    trigger_events: { ...p.trigger_events, execution_fired: e.target.checked },
                  }))
                }
              />
            }
            label="An execution webhook fires (a trade is sent)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={prefs.trigger_events.execution_failed}
                onChange={(e) =>
                  setPrefs((p) => ({
                    ...p,
                    trigger_events: { ...p.trigger_events, execution_failed: e.target.checked },
                  }))
                }
              />
            }
            label="An execution webhook fails"
          />
          <FormControlLabel
            control={
              <Switch
                checked={prefs.trigger_events.indicator_activated}
                onChange={(e) =>
                  setPrefs((p) => ({
                    ...p,
                    trigger_events: { ...p.trigger_events, indicator_activated: e.target.checked },
                  }))
                }
              />
            }
            label="An individual indicator/slot triggers"
          />
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        {/* In-app */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <VolumeUpIcon sx={{ mr: 1 }} fontSize="small" />
          <Typography variant="subtitle2">In-app (this browser)</Typography>
        </Box>
        <FormGroup row sx={{ gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={prefs.dashboard_alerts.popup_enabled}
                onChange={(e) =>
                  setPrefs((p) => ({
                    ...p,
                    dashboard_alerts: { ...p.dashboard_alerts, popup_enabled: e.target.checked },
                  }))
                }
              />
            }
            label="Pop-up notifications"
          />
          <FormControlLabel
            control={
              <Switch
                checked={prefs.dashboard_alerts.sound_enabled}
                onChange={(e) =>
                  setPrefs((p) => ({
                    ...p,
                    dashboard_alerts: { ...p.dashboard_alerts, sound_enabled: e.target.checked },
                  }))
                }
              />
            }
            label="Sound"
          />
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        {/* Email */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <EmailIcon sx={{ mr: 1 }} fontSize="small" />
          <Typography variant="subtitle2">Email</Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={prefs.email_notifications.enabled}
              onChange={(e) =>
                setPrefs((p) => ({
                  ...p,
                  email_notifications: { ...p.email_notifications, enabled: e.target.checked },
                }))
              }
            />
          }
          label="Send email notifications"
        />
        {prefs.email_notifications.enabled && (
          <TextField
            fullWidth
            size="small"
            label="Email addresses (comma separated)"
            placeholder="you@example.com, partner@example.com"
            value={emailAddressesText}
            onChange={(e) => setEmailAddressesText(e.target.value)}
            helperText="Leave blank to use your account email."
            sx={{ mt: 1 }}
          />
        )}

        {feedback && (
          <Alert severity={feedback.severity} sx={{ mt: 2 }}>
            {feedback.text}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={handleSave} disabled={saving || testing}>
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
          <Button variant="outlined" onClick={handleTest} disabled={saving || testing}>
            {testing ? 'Sending…' : 'Send test notification'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
